import React, { useRef, useCallback, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Tooltip, Chip, Typography, Paper, Button, Stack,
  Popper, ClickAwayListener, List, ListItem, ListItemButton, ListItemText,
  CircularProgress, InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO, PARAMS_PRODUCTO_OPERATIVO } from '../../../services/productoService';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import inventarioInformes from '../../../services/inventarioInformes';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import { formatCurrency } from '../../../utils/formatters';
import { POSLinea, SuggestionItem } from './types';
import FacturaPOSStockPopover from './FacturaPOSStockPopover';
import { getApiErrorMessage } from '../../../utils/apiError';
import { esEntradaCodigoBarra } from '../../../utils/codigoBarraVenta';
import {
  aPrecioDisplay,
  multiplicadorIvaProducto,
  round2,
  subtotalLineaDisplay,
} from './preciosIvaDisplay';
import POSPrecioUnitarioField from './POSPrecioUnitarioField';

interface Props {
  lineas: POSLinea[];
  nroLista?: string;
  tipoIvaEmision?: string;
  productosIvaCache?: Record<string, unknown>;
  preciosConIva?: boolean;
  readOnly?: boolean;
  /** Muestra precioUnitarioFinal/subtotalFinal del documento cargado (edición/lectura). */
  usarPreciosPersistidosEnLineas?: boolean;
  onLineaChange: (id: string, field: keyof POSLinea, value: string | number) => void;
  onLineaFilled: (id: string, data: Partial<POSLinea>) => void;
  /** Registra el producto completo para cálculo de IVA (evita esperar al useEffect de cabecera). */
  onProductoIvaLoaded?: (codigoArticulo: string, producto: unknown) => void;
  onEliminarLinea: (id: string) => void;
  onDuplicarLinea: (id: string) => void;
  onAgregarLineaVacia: () => void;
}

const NUMERIC_COLS = ['cantidad', 'descuento'];
const NAV_COLS = ['codigoArticulo', 'descripcionArticulo', 'cantidad', 'precioUnitario', 'descuento', 'notas'];
const MSG_CODIGO_INEXISTENTE = 'No existe en el maestro de artículos';

const lineaArticuloResuelto = (linea: POSLinea) =>
  Boolean(String(linea.descripcionArticulo ?? '').trim());

const datosArticuloVacios = (): Partial<POSLinea> => ({
  descripcionArticulo: '',
  precioUnitario: 0,
  um: '',
  stockDisponible: null,
});

const EMPTY_PRODUCTOS_CACHE: Record<string, unknown> = {};

/** Muestra el contenido completo del campo al pasar el mouse (como en Buscar pendientes). */
const CampoConTooltip: React.FC<{
  title?: string | null;
  children: React.ReactElement;
}> = ({ title, children }) => {
  const texto = String(title ?? '').trim();
  return (
    <Tooltip
      title={texto}
      disableHoverListener={!texto}
      placement="top"
      enterDelay={400}
      slotProps={{
        tooltip: { sx: { maxWidth: 420, whiteSpace: 'pre-wrap' } },
      }}
    >
      <Box component="span" sx={{ display: 'block', width: '100%' }}>
        {children}
      </Box>
    </Tooltip>
  );
};

const StockBadge: React.FC<{ stock?: number | null; onClick?: (e: React.MouseEvent<HTMLElement>) => void }> = ({ stock, onClick }) => {
  const sx = { height: 18, fontSize: '0.65rem', cursor: onClick ? 'pointer' : 'default' };
  if (stock == null)
    return <Chip label="?" size="small" variant="outlined" onClick={onClick} sx={sx} />;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" onClick={onClick} sx={sx} />;
  if (stock < 10)
    return <Chip label={String(stock)} size="small" color="warning" onClick={onClick} sx={sx} />;
  return <Chip label={String(stock)} size="small" color="success" onClick={onClick} sx={sx} />;
};

// ── Helpers para resolución de precio/stock ──────────────────────────────────
async function resolverPrecioConLista(
  producto: any,
  nroLista?: string,
): Promise<number> {
  let precio = obtenerPrecioBaseProducto(producto);
  if (nroLista) {
    try {
      const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
        nroLista,
        producto.codigoArticulo,
      );
      const res = resolverPrecioDesdeLista(producto, reg);
      if (res.tipo !== 'sinPrecio') precio = res.precioLista;
    } catch {
      /* sin precio en lista, usa precio catalogo */
    }
  }
  return precio;
}

async function obtenerStock(codigoArticulo: string): Promise<number | null> {
  try {
    const res = await inventarioInformes.getStockPorDeposito({
      codigoArticulo,
      pageSize: 50,
    });
    const rows: any[] = (res?.data ?? []).filter(
      (r: any) => (r.codigoArticulo ?? '').trim() === codigoArticulo.trim(),
    );
    if (rows.length === 0) return null;
    return rows.reduce((acc: number, r: any) => acc + (Number(r.disponibleDeposito) || 0), 0);
  } catch {
    return null;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const FacturaPOSLineasGrid: React.FC<Props> = ({
  lineas,
  nroLista,
  tipoIvaEmision = '',
  productosIvaCache = EMPTY_PRODUCTOS_CACHE,
  preciosConIva = false,
  readOnly = false,
  usarPreciosPersistidosEnLineas = false,
  onLineaChange,
  onLineaFilled,
  onProductoIvaLoaded,
  onEliminarLinea,
  onDuplicarLinea,
  onAgregarLineaVacia,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);

  // Estado para resolución de código
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [searchState, setSearchState] = useState<{
    id: string;
    results: SuggestionItem[];
    anchorEl: HTMLElement | null;
  } | null>(null);

  // Estado para popover de stock por depósito
  const [stockPopover, setStockPopover] = useState<{
    codigoArticulo: string;
    descripcion: string;
    anchorEl: HTMLElement | null;
  } | null>(null);
  const [codigoErrors, setCodigoErrors] = useState<Record<string, string>>({});

  const setCodigoError = useCallback((id: string, message: string) => {
    setCodigoErrors((prev) => ({ ...prev, [id]: message }));
  }, []);

  const clearCodigoError = useCallback((id: string) => {
    setCodigoErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const marcarErrorCodigo = useCallback(
    (id: string, message: string) => {
      onLineaFilled(id, datosArticuloVacios());
      setCodigoError(id, message);
    },
    [onLineaFilled, setCodigoError],
  );

  const marcarCodigoInexistente = useCallback(
    (id: string) => {
      marcarErrorCodigo(id, MSG_CODIGO_INEXISTENTE);
    },
    [marcarErrorCodigo],
  );

  const focusCell = useCallback((rowIdx: number, colName: string) => {
    const el = tableRef.current?.querySelector<HTMLInputElement>(
      `[data-row="${rowIdx}"][data-col="${colName}"]`,
    );
    el?.focus();
    el?.select();
  }, []);

  const obtenerMultiplicadorDisplay = useCallback(
    (codigoArticulo?: string, productoFallback?: unknown): number => {
      if (!preciosConIva) return 1;
      const codigo = String(codigoArticulo ?? '').trim();
      return multiplicadorIvaProducto(productosIvaCache[codigo] ?? productoFallback, tipoIvaEmision);
    },
    [preciosConIva, productosIvaCache, tipoIvaEmision],
  );

  // ── Resolución de código al presionar Enter ──────────────────────────────
  const handleResolveCodigo = useCallback(
    async (id: string, codigo: string, inputEl: HTMLElement | null) => {
      if (!codigo.trim()) return;
      setResolvingId(id);
      setSearchState(null);

      const rowIdx = lineas.findIndex((l) => l.id === id);

      try {
        const trimmed = codigo.trim();

        if (esEntradaCodigoBarra(trimmed)) {
          try {
            const resolved = await productoService.resolverCodigoBarra(trimmed, nroLista);
            const cod = String(resolved.codigoArticulo ?? '').trim();
            if (cod && resolved.producto) onProductoIvaLoaded?.(cod, resolved.producto);
            const stock = await obtenerStock(cod);
            clearCodigoError(id);
            onLineaFilled(id, {
              codigoArticulo: resolved.codigoArticulo,
              descripcionArticulo: resolved.descripcion,
              cantidad: resolved.cantidad,
              precioUnitario: resolved.precioUnitario,
              um: resolved.um ?? '',
              stockDisponible: stock,
            });
            const colFocus = resolved.origen.startsWith('plu_') ? 'precioUnitario' : 'cantidad';
            setTimeout(() => focusCell(rowIdx, colFocus), 60);
            return;
          } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const apiMsg = getApiErrorMessage(err, MSG_CODIGO_INEXISTENTE);
            if (status === 409 || status === 400) {
              marcarErrorCodigo(id, apiMsg);
            } else {
              marcarCodigoInexistente(id);
            }
            return;
          }
        }

        // Intento lookup exacto por código
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        const cod = String(producto.codigoArticulo ?? codigo).trim();
        if (cod) onProductoIvaLoaded?.(cod, producto);
        const precio = await resolverPrecioConLista(producto, nroLista);
        const stock = await obtenerStock(producto.codigoArticulo);

        clearCodigoError(id);
        onLineaFilled(id, {
          codigoArticulo: producto.codigoArticulo,
          descripcionArticulo: producto.descripcion,
          precioUnitario: precio,
          um: (producto as any).um ?? (producto as any).unidadMedida ?? '',
          stockDisponible: stock,
        });

        // Posicionarse en cantidad
        setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const apiMsg = getApiErrorMessage(err, '');
        if (status === 409 && apiMsg) {
          marcarErrorCodigo(id, apiMsg);
          return;
        }
        // No encontrado por código → buscar por descripción (solo para elegir artículo existente)
        try {
          const res = await productoService.getProductos({
            searchTerm: codigo.trim(),
            pageNumber: 1,
            pageSize: 8,
            ...PARAMS_PRODUCTO_OPERATIVO,
          });
          const items: any[] = res.items ?? [];
          const enriched: SuggestionItem[] = items.map((p) => ({
            codigoArticulo: p.codigoArticulo,
            descripcion: p.descripcion,
            precioUnitario: obtenerPrecioBaseProducto(p),
            um: p.um ?? p.unidadMedida ?? '',
            stockDisponible: null,
            productoIva: p,
          }));
          if (enriched.length === 1) {
            // Un único resultado → seleccionar directo
            try {
              const productoUnico = await productoService.getProducto(enriched[0].codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
              const codU = String(productoUnico.codigoArticulo ?? enriched[0].codigoArticulo).trim();
              if (codU) onProductoIvaLoaded?.(codU, productoUnico);
            } catch {
              /* cache IVA se completará por useEffect de cabecera */
            }
            const precio = await resolverPrecioConLista(enriched[0], nroLista);
            const stock = await obtenerStock(enriched[0].codigoArticulo);
            clearCodigoError(id);
            onLineaFilled(id, {
              codigoArticulo: enriched[0].codigoArticulo,
              descripcionArticulo: enriched[0].descripcion,
              precioUnitario: precio,
              um: enriched[0].um,
              stockDisponible: stock,
            });
            setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
          } else if (enriched.length > 1) {
            clearCodigoError(id);
            setSearchState({ id, results: enriched, anchorEl: inputEl });
          } else {
            marcarCodigoInexistente(id);
          }
        } catch {
          marcarCodigoInexistente(id);
        }
      } finally {
        setResolvingId(null);
      }
    },
    [lineas, nroLista, onLineaFilled, onProductoIvaLoaded, focusCell, clearCodigoError, marcarCodigoInexistente, marcarErrorCodigo],
  );

  const handleCodigoBlur = useCallback(
    async (id: string, codigo: string) => {
      const trimmed = codigo.trim();
      if (!trimmed || readOnly) return;
      const linea = lineas.find((l) => l.id === id);
      if (!linea || lineaArticuloResuelto(linea)) return;
      if (esEntradaCodigoBarra(trimmed)) {
        await handleResolveCodigo(id, trimmed, null);
        return;
      }
      try {
        await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        await handleResolveCodigo(id, trimmed, null);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const apiMsg = getApiErrorMessage(err, '');
        if (status === 409 && apiMsg) {
          marcarErrorCodigo(id, apiMsg);
        } else {
          marcarCodigoInexistente(id);
        }
      }
    },
    [lineas, readOnly, handleResolveCodigo, marcarCodigoInexistente, marcarErrorCodigo],
  );

  const handleSearchSelect = useCallback(
    async (item: SuggestionItem) => {
      if (!searchState) return;
      const { id } = searchState;
      setSearchState(null);
      setResolvingId(id);
      const rowIdx = lineas.findIndex((l) => l.id === id);

      try {
        const producto = await productoService.getProducto(item.codigoArticulo, OPTIONS_PRODUCTO_OPERATIVO);
        const cod = String(producto.codigoArticulo ?? item.codigoArticulo).trim();
        if (cod) onProductoIvaLoaded?.(cod, producto);
      } catch {
        /* cache IVA se completará por useEffect de cabecera */
      }
      const precio = await resolverPrecioConLista(item, nroLista);
      const stock = await obtenerStock(item.codigoArticulo);
      clearCodigoError(id);
      onLineaFilled(id, {
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        precioUnitario: precio,
        um: item.um,
        stockDisponible: stock,
      });
      setResolvingId(null);
      setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
    },
    [searchState, lineas, nroLista, onLineaFilled, onProductoIvaLoaded, focusCell, clearCodigoError],
  );

  // ── Navegación por teclado ───────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      rowIdx: number,
      colName: string,
    ) => {
      if (e.key === 'Escape') {
        setSearchState(null);
        return;
      }
      if (readOnly) return;
      const colIdx = NAV_COLS.indexOf(colName);

      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        setSearchState(null);
        const isLastCol = colIdx === NAV_COLS.length - 1;
        if (!isLastCol) {
          focusCell(rowIdx, NAV_COLS[colIdx + 1]);
        } else if (rowIdx < lineas.length - 1) {
          focusCell(rowIdx + 1, NAV_COLS[0]);
        } else {
          onAgregarLineaVacia();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (colName === 'codigoArticulo') {
          const inputEl = (e.target as HTMLInputElement).closest(
            '.MuiFormControl-root',
          ) as HTMLElement | null;
          const id = lineas[rowIdx]?.id;
          if (id) handleResolveCodigo(id, (e.target as HTMLInputElement).value, inputEl);
        } else {
          if (rowIdx < lineas.length - 1) {
            focusCell(rowIdx + 1, NAV_COLS[0]);
          } else {
            onAgregarLineaVacia();
          }
        }
      }
    },
    [lineas, focusCell, onAgregarLineaVacia, handleResolveCodigo, readOnly],
  );

  const handleChange = (id: string, field: keyof POSLinea, raw: string) => {
    if (field === 'codigoArticulo') {
      clearCodigoError(id);
      const linea = lineas.find((l) => l.id === id);
      onLineaChange(id, field, raw);
      if (linea && lineaArticuloResuelto(linea)) {
        onLineaFilled(id, datosArticuloVacios());
      }
      return;
    }
    if (NUMERIC_COLS.includes(field as string)) {
      const v = parseFloat(raw.replace(',', '.'));
      const numericValue = isNaN(v) ? 0 : v;
      onLineaChange(id, field, numericValue);
    } else {
      onLineaChange(id, field, raw);
    }
  };

  const cellSx = {
    p: '3px 4px',
    border: 0,
    borderBottom: '1px solid',
    borderColor: 'divider',
    verticalAlign: 'middle',
  };
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };
  const numInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { textAlign: 'right' as const, padding: '4px 6px', fontSize: '0.82rem' },
    min: 0,
  });
  const textInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { padding: '4px 6px', fontSize: '0.82rem' },
  });

  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.78rem',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  } as const;

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer
          sx={{
            overflowX: 'auto',
            pb: 2,
            boxSizing: 'border-box',
          }}
        >
        <Table
          size="small"
          ref={tableRef}
          sx={{
            tableLayout: 'fixed',
            /* Evita que columnas colapsen y los títulos se superpongan en layouts angostos (p. ej. columna md ~65%). */
            minWidth: 980,
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>Código</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 140, width: '22%' }}>Descripción</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 50 }}>U/M</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 78 }}>Stock</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 80 }}>Cant.</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 110 }}>
                {preciosConIva ? 'Precio Final' : 'Precio'}
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 72 }}>Desc%</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100, textAlign: 'right' }}>
                {preciosConIva ? 'Subtotal Final' : 'Subtotal'}
              </TableCell>
              <TableCell sx={{ width: 56 }} />
              <TableCell sx={{ ...headerCellSx, width: 140 }}>Notas</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 5, color: 'text.disabled', fontSize: '0.9rem' }}
                >
                  Buscá un artículo arriba para agregarlo a la factura
                </TableCell>
              </TableRow>
            )}
            {lineas.map((linea, rowIdx) => {
              const isResolving = resolvingId === linea.id;
              const multiplicadorDisplay = obtenerMultiplicadorDisplay(linea.codigoArticulo);
              const usarPreciosPersistidos =
                (readOnly || usarPreciosPersistidosEnLineas) &&
                preciosConIva &&
                linea.precioUnitarioFinal != null &&
                linea.precioUnitarioFinal > 0;
              const precioUnitarioDisplay = usarPreciosPersistidos
                ? linea.precioUnitarioFinal!
                : preciosConIva
                  ? aPrecioDisplay(linea.precioUnitario, multiplicadorDisplay)
                  : linea.precioUnitario;
              const subtotalDisplay = usarPreciosPersistidos
                ? (linea.subtotalFinal ?? round2(linea.precioUnitarioFinal! * linea.cantidad))
                : preciosConIva
                  ? subtotalLineaDisplay(linea.subtotal, multiplicadorDisplay)
                  : linea.subtotal;
              return (
                <TableRow key={linea.id} hover>
                  {/* ── Código (con resolución + spinner) ── */}
                  <TableCell sx={cellSx}>
                    <Box sx={{ position: 'relative' }}>
                      <CampoConTooltip title={linea.codigoArticulo}>
                        <TextField
                        size="small"
                        fullWidth
                        value={linea.codigoArticulo}
                        onChange={(e) => handleChange(linea.id, 'codigoArticulo', e.target.value)}
                        onBlur={(e) => void handleCodigoBlur(linea.id, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'codigoArticulo')}
                        inputProps={textInputProps(rowIdx, 'codigoArticulo')}
                        error={Boolean(codigoErrors[linea.id])}
                        InputProps={
                          isResolving
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <CircularProgress size={12} />
                                  </InputAdornment>
                                ),
                                readOnly,
                              }
                            : { readOnly }
                        }
                        disabled={isResolving}
                        sx={{
                          ...inputSx,
                          m: 0,
                          '& input': { fontFamily: 'monospace' },
                        }}
                      />
                      </CampoConTooltip>
                      {codigoErrors[linea.id] ? (
                        <Typography
                          component="span"
                          variant="caption"
                          color="error"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '100%',
                            mt: 0.25,
                            fontSize: '0.65rem',
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {codigoErrors[linea.id]}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>

                  {/* ── Descripción ── */}
                  <TableCell sx={{ ...cellSx, minWidth: 140 }}>
                    <CampoConTooltip title={linea.descripcionArticulo}>
                      <TextField
                        size="small"
                        fullWidth
                        value={linea.descripcionArticulo}
                        onChange={(e) =>
                          handleChange(linea.id, 'descripcionArticulo', e.target.value)
                        }
                        onKeyDown={(e) =>
                          handleKeyDown(e as any, rowIdx, 'descripcionArticulo')
                        }
                        inputProps={textInputProps(rowIdx, 'descripcionArticulo')}
                        InputProps={{ readOnly }}
                        sx={inputSx}
                      />
                    </CampoConTooltip>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {linea.um || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <StockBadge
                      stock={linea.stockDisponible}
                      onClick={
                        linea.codigoArticulo
                          ? (e) =>
                              setStockPopover({
                                codigoArticulo: linea.codigoArticulo,
                                descripcion: linea.descripcionArticulo,
                                anchorEl: e.currentTarget,
                              })
                          : undefined
                      }
                    />
                  </TableCell>

                  {/* ── Cantidad ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.cantidad || ''}
                      onChange={(e) => handleChange(linea.id, 'cantidad', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'cantidad')}
                      inputProps={numInputProps(rowIdx, 'cantidad')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  {/* ── Precio ── */}
                  <TableCell sx={cellSx}>
                    {usarPreciosPersistidos ? (
                      <TextField
                        size="small"
                        fullWidth
                        type="text"
                        value={precioUnitarioDisplay || ''}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'precioUnitario')}
                        inputProps={numInputProps(rowIdx, 'precioUnitario')}
                        InputProps={{ readOnly: true }}
                        sx={inputSx}
                      />
                    ) : (
                      <POSPrecioUnitarioField
                        precioNeto={linea.precioUnitario}
                        preciosConIva={preciosConIva}
                        multiplicador={multiplicadorDisplay}
                        onChange={(v) => onLineaChange(linea.id, 'precioUnitario', v)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'precioUnitario')}
                        inputProps={numInputProps(rowIdx, 'precioUnitario')}
                        readOnly={readOnly}
                        sx={inputSx}
                      />
                    )}
                  </TableCell>

                  {/* ── Descuento ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.descuento || ''}
                      onChange={(e) => handleChange(linea.id, 'descuento', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'descuento')}
                      inputProps={{ ...numInputProps(rowIdx, 'descuento'), max: 100 }}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, textAlign: 'right', verticalAlign: 'middle' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={linea.subtotal > 0 ? 'text.primary' : 'text.disabled'}
                    >
                      {formatCurrency(subtotalDisplay)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <Stack direction="row" spacing={0}>
                      <Tooltip title="Duplicar línea">
                        <IconButton size="small" onClick={() => onDuplicarLinea(linea.id)} disabled={readOnly}>
                          <DuplicateIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onEliminarLinea(linea.id)}
                          disabled={readOnly}
                        >
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <CampoConTooltip title={linea.notas}>
                      <TextField
                        size="small"
                        fullWidth
                        value={linea.notas ?? ''}
                        onChange={(e) => handleChange(linea.id, 'notas', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'notas')}
                        inputProps={textInputProps(rowIdx, 'notas')}
                        InputProps={{ readOnly }}
                        sx={inputSx}
                      />
                    </CampoConTooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {/* ── Dropdown de búsqueda (Popper, fuera del TableContainer) ── */}
      {searchState && (
        <ClickAwayListener onClickAway={() => setSearchState(null)}>
          <Popper
            open
            anchorEl={searchState.anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400, minWidth: 380 }}
          >
            <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' }}>
              <List dense disablePadding>
                {searchState.results.map((s, idx) => {
                  const multiplicador = obtenerMultiplicadorDisplay(s.codigoArticulo, s.productoIva);
                  const precioDisplay = preciosConIva
                    ? aPrecioDisplay(s.precioUnitario, multiplicador)
                    : s.precioUnitario;
                  return (
                    <ListItem
                      key={s.codigoArticulo}
                      disablePadding
                      divider={idx < searchState.results.length - 1}
                    >
                      <ListItemButton
                        onClick={() => handleSearchSelect(s)}
                        sx={{ py: 0.75, px: 1.5 }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ minWidth: 80, fontFamily: 'monospace', flexShrink: 0 }}
                              >
                                {s.codigoArticulo}
                              </Typography>
                              <Typography variant="body2" flex={1} noWrap>
                                {s.descripcion}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="primary.main" fontWeight={600}>
                              {preciosConIva ? 'Precio Final: ' : ''}
                              {formatCurrency(precioDisplay)}
                              {preciosConIva ? ' c/imp.' : ''}
                              {s.um ? ` / ${s.um}` : ''}
                            </Typography>
                          }
                          disableTypography
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      <Box mt={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={onAgregarLineaVacia} variant="text" disabled={readOnly}>
          Agregar línea vacía
        </Button>
      </Box>

      {/* ── Popover stock por depósito ── */}
      {stockPopover && (
        <FacturaPOSStockPopover
          codigoArticulo={stockPopover.codigoArticulo}
          descripcion={stockPopover.descripcion}
          anchorEl={stockPopover.anchorEl}
          onClose={() => setStockPopover(null)}
        />
      )}
    </Box>
  );
};

export default FacturaPOSLineasGrid;
